import type { PayPalSDK } from './types';

export const PAYPAL_MESSAGE_TIMEOUT_MS = 10000;
export type MessageStatus = 'loading' | 'ready' | 'unavailable';

// Own the message target so late SDK callbacks cannot repopulate a failed or superseded message.
export function renderPayPalMessage(
  sdk: Pick<PayPalSDK, 'Messages'>,
  container: HTMLElement,
  amount: string,
  onStatusChange: (status: MessageStatus) => void,
) {
  let disposed = false;
  let failed = false;
  let ready = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let observer: MutationObserver | undefined;
  const target = document.createElement('div');
  // Deliberately omit data-pp-message: only our explicit render call should start messaging.
  // PayPal's automatic DOM observer otherwise bypasses our callbacks and timeout.
  Object.entries({
    'data-pp-style-layout': 'text',
    'data-pp-style-logo-type': 'inline',
    'data-pp-style-text-color': 'black',
    'data-pp-style-text-size': '12',
    'data-pp-amount': amount,
    'data-pp-placement': 'payment',
  }).forEach(([name, value]) => target.setAttribute(name, value));
  container.appendChild(target);
  onStatusChange('loading');

  const confirmRendered = () => {
    if (disposed || failed || ready) return;
    ready = true;
    clearTimeout(timer);
    observer?.disconnect();
    onStatusChange('ready');
  };
  const fail = (reason: string) => {
    if (disposed || failed) return;
    failed = true;
    clearTimeout(timer);
    observer?.disconnect();
    target.remove();
    onStatusChange('unavailable');
    // Do not log SDK error objects, which can contain checkout/customer data.
    console.warn(`[PayPal messaging] ${reason}; continuing without promotional messaging.`);
  };

  const messages = sdk.Messages;
  if (!messages) {
    fail('Messages component unavailable');
  } else {
    timer = setTimeout(() => fail('Rendering timed out'), PAYPAL_MESSAGE_TIMEOUT_MS);
    // Some SDK versions render successfully without calling onRender. PayPal sizes its
    // message iframe when content is ready; an empty/hidden iframe is not enough.
    observer = new MutationObserver(() => {
      if (Array.from(target.querySelectorAll('iframe')).some(frame => frame.getBoundingClientRect().height > 0)) {
        confirmRendered();
      }
    });
    observer.observe(target, { childList: true, subtree: true, attributes: true });
    void Promise.resolve()
      .then(() => {
        if (disposed || failed) return;
        return messages({
          amount,
          placement: 'payment',
          onRender: confirmRendered,
        }).render(target);
      })
      .catch(() => fail('Rendering failed'));
  }

  return () => {
    disposed = true;
    clearTimeout(timer);
    observer?.disconnect();
    target.remove();
  };
}
